import {ChangeDetectionStrategy, Component, DestroyRef, Inject, Injectable, isDevMode, signal} from "@angular/core";
import {GruppeId, GruppeMedlemListeModell, HtmlTekst, MøteId, MøteModell, MøtepubliseringSpesifikasjon, MøtePubliseringValideringsfeil} from "felles-ng2/tjenester/ressurser/generertNg2HttpKlient";
import {initDebugger} from "felles-ng2/støttefunksjoner/debug";
import {MAT_DIALOG_DATA, MatDialog, MatDialogActions, MatDialogContent, MatDialogRef} from "@angular/material/dialog";
import {MatButton, MatIconButton} from "@angular/material/button";
import {MatFormField, MatLabel, MatPrefix} from "@angular/material/form-field";
import {MatIcon} from "@angular/material/icon";
import {MatToolbar} from "@angular/material/toolbar";
import {FormControl, FormGroup, ReactiveFormsModule} from "@angular/forms";
import {TipsKomponent} from "web-ng2/delt/komponenter/tips/tips";
import {TranslateModule} from "@ngx-translate/core";
import {VisSpinnerDirektiv} from "web-ng2/delt/direktiver/button--visSpinner/button--visSpinner";
import {MatSlideToggle} from "@angular/material/slide-toggle";
import {FeltValidatorer} from "felles-ng2/infrastruktur/angular/feltValidatorer";
import {GruppemedlemDatatjeneste} from "felles-ng2/tjenester/datalager/GruppemedlemDatatjeneste";
import {Observable} from "rxjs";
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";
import {VelgMedlemmerKomponent} from "web-ng2/delt/komponenter/skjema/velg-medlemmer/velg-medlemmer";
import {medlemmerSomDeltarPåMøte} from "felles-ng2/modell/møteModellHjelpere";
import {MøteDatatjeneste} from "felles-ng2/tjenester/datalager/MøteDatatjeneste";
import {TypetValideringsresultatModell} from "felles-ng2/tjenester/ressurser/ng2HttpKlientTilleggstyper";
import {MeldingTilBruker} from "felles/tjenester/MeldingTilBruker";
import {i18n, Oversettelser} from "felles-ng2/tjenester/i18n/Oversettelser";
import {MatProgressBar} from "@angular/material/progress-bar";
import {asynkArbeidPågårSignal, standardMinimumVarighetKonfig} from "felles-ng2/tjenester/AsynkArbeidPågårSignal";
import {Navigasjon} from "web-ng2/tjenester/Navigasjon";
import {MeldingSendtIkonKomponent} from "web-ng2/delt/komponenter/melding-sendt-ikon/melding-sendt-ikon";
import {tomMøteModell} from "felles-ng2/modell/tommeModeller";
import {RiktekstRedigeringKomponent} from "web-ng2/delt/komponenter/skjema/riktekst-redigering/riktekst-redigering";
import {erIkkeNil} from "felles-ng2/støttefunksjoner/nilsjekk";
import {medlemmerMedTilgangMedBrukertilknytning} from "felles-ng2/modell/medlemModellHjelpere";

const debug = initDebugger('MøtePubliserDialogKomponent');

/**
 * Dialog for publisering av møte
 */
@Component({
    selector: 'mote-publiser-dialog',
    templateUrl: 'møte-publiser-dialog.html',
    styleUrl: 'møte-publiser-dialog.scss',
    imports: [
        MatButton,
        MatDialogActions,
        MatDialogContent,
        MatFormField,
        MatIcon,
        MatIconButton,
        MatLabel,
        MatToolbar,
        ReactiveFormsModule,
        TipsKomponent,
        TranslateModule,
        VisSpinnerDirektiv,
        MatSlideToggle,
        VelgMedlemmerKomponent,
        MatPrefix,
        MatProgressBar,
        MeldingSendtIkonKomponent,
        RiktekstRedigeringKomponent
    ],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class MøtePubliserDialogKomponent {

    static visPubliserMøte(dialog: MatDialog, møte: Promise<MøteModell>): MatDialogRef<MøtePubliserDialogKomponent, MøtePubliserDialogRespons> {
        return dialog.open(MøtePubliserDialogKomponent, {data: {møte, type: 'møte'} as DialogParametre, disableClose: true, ariaLabelledBy: 'møte-publiser-dialog-tittel', minHeight: 500, maxWidth: 700});
    }

    static visPubliserProtokoll(dialog: MatDialog, møte: Promise<MøteModell>): MatDialogRef<MøtePubliserDialogKomponent, MøtePubliserDialogRespons> {
        return dialog.open(MøtePubliserDialogKomponent, {data: {møte, type: 'protokoll'} as DialogParametre, disableClose: true, ariaLabelledBy: 'møte-publiser-dialog-tittel', minHeight: 500, maxWidth: 700});
    }

    moete = signal<MøteModell>(tomMøteModell());
    modus = signal<'skjema' | 'kvittering'>('skjema');
    laster = asynkArbeidPågårSignal();
    lagrer = asynkArbeidPågårSignal(standardMinimumVarighetKonfig());

    skjema = new FormGroup({
        sendMelding: new FormControl(true, {nonNullable: true}),
        beskjed: new FormControl<HtmlTekst>('', {nonNullable: true, validators: FeltValidatorer.htmlTekst}),
        valgteMottakere: new FormControl<GruppeMedlemListeModell[]>([], {nonNullable: true}),
    });
    tilgjengeligeMottakere: GruppeMedlemListeModell[] = [];
    type: PubliseringType;

    constructor(@Inject(MAT_DIALOG_DATA) parametre: DialogParametre,
                private dialogRef: MatDialogRef<MøtePubliserDialogKomponent, MøtePubliserDialogRespons>,
                private avhengigheter: MøtePubliserDialogKomponentAvhengigheter,
                private meldingTilBruker: MeldingTilBruker,
                private oversettelser: Oversettelser,
                private navigasjon: Navigasjon,
                private destroyRef: DestroyRef) {
        this.type = parametre.type;
        this.initMøte(parametre.møte);
    }

    async publiser() {
        await this.lagrer.utfør(async () => {
            if (this.type == 'møte') await this.publiserMøte();
            else await this.publiserProtokoll();

            this.modus.set('kvittering');
        });
    }

    avbryt() {
        this.dialogRef.close(false);
    }

    ferdig() {
        this.dialogRef.close(true);
    }

    gaaTilHendelseslogg() {
        this.ferdig();
        this.navigasjon.gåTilMøteHendelseslogg(this.moete().id);
    }

    //private hjelpemetoder

    private async initMøte(møtepromise: Promise<MøteModell>) {
        this.moete.set(await this.laster.utfør(() => møtepromise));
        const laster = this.laster.start();
        this.avhengigheter.medlemmer(this.moete().eiendeGruppeId)
          .pipe(
            takeUntilDestroyed(this.destroyRef),
            laster.avsluttVedLeveranse())
          .subscribe(medlemmer => this.initTilgjengeligeMottakere(medlemmer));
    }

    private initTilgjengeligeMottakere(medlemmer: GruppeMedlemListeModell[]) {
        this.tilgjengeligeMottakere = medlemmerMedTilgangMedBrukertilknytning(medlemmer)
        this.skjema.controls.valgteMottakere.setValue(medlemmerSomDeltarPåMøte(this.moete(), medlemmer));
    }

    private async publiserMøte() {
        const valideringsresultat = await this.avhengigheter.publiserMøte(this.moete().id, this.somSpesifikasjon())
        if (valideringsresultat.harFeil) {
            this.meldingTilBruker.feilmelding(this.oversettelser.oversett(i18n('En feil oppstod under publisering av møte')));
            if (isDevMode()) throw new Error(`Uventet valideringsfeil ved publisering av møte, valideringsresultat: ${JSON.stringify(valideringsresultat)}`);
            this.avbryt();
        }
        /*
        @todo mangler
           switch (valideringsresultat.feil[0].feilkode){
            case l.MøtePubliseringValideringsfeil.åpent_for_avstemning:
                return this.meldingTilBruker.feilmelding('Møtet er åpent for avstemning, du kan derfor ikke oppheve møte-pulisering', Varighet.sideskift, () => {this.navigasjon.gåTilMøteAvstemning(møteId); return true;}, false)
            case l.MøtePubliseringValideringsfeil.protokoll_låst: //den kan normalt ikke nås da "opphev publisering" knappen ikke vises dersom protokoll er låst
                return this.meldingTilBruker.feilmelding('Protokoll fra møte er låst, du kan derfor ikke oppheve møte-pulisering', Varighet.sideskift, null, false)
        }
         */
    }

    private async publiserProtokoll() {
        await this.avhengigheter.publiserProtokoll(this.moete().id, this.somSpesifikasjon());
    }

    private somSpesifikasjon(): MøtepubliseringSpesifikasjon {
        const {sendMelding, beskjed, valgteMottakere} = this.skjema.value;
        return {
            erPublisert: true,
            sendMelding,
            beskjed,
            mottakere: valgteMottakere!.map(medlem => medlem.id)
        };
    }
}

type PubliseringType = 'møte' | 'protokoll';

export type MøtePubliserDialogRespons = boolean;

export type DialogParametre = { møte: Promise<MøteModell>; type: PubliseringType; };

@Injectable()
export class MøtePubliserDialogKomponentAvhengigheter {

    constructor(private møteDatatjeneste: MøteDatatjeneste, private gruppemedlemDatatjeneste: GruppemedlemDatatjeneste) {
    }

    medlemmer(gruppeId: GruppeId): Observable<GruppeMedlemListeModell[]> {
        return this.gruppemedlemDatatjeneste.finnTilgjengelige(gruppeId);
    }

    publiserMøte(id: MøteId, spesifikasjon: MøtepubliseringSpesifikasjon): Promise<TypetValideringsresultatModell<MøtePubliseringValideringsfeil>> {
        return this.møteDatatjeneste.oppdaterMøtePublisering(id, spesifikasjon);
    }

    async publiserProtokoll(id: MøteId, spesifikasjon: MøtepubliseringSpesifikasjon): Promise<void> {
        await this.møteDatatjeneste.oppdaterProtokollPublisering(id, spesifikasjon);
    }
}
